import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from "react";
import { useLocation, useHistory } from "react-router-dom";
import moment from "moment-timezone";
import { Spinner } from "@themesberg/react-bootstrap";

import Table from "./partials/Table";
import FilterForm from "./partials/FilterForm";
import { useCookies } from "react-cookie";
import { getList } from "../../../controller/api/__classic__/result";
import { calculatePage } from "../../../helper/helper";

export const Context = React.createContext(null);

const initialData = {
  filter: {
    from: null,
    to: null,
    channelId: "",
    status: "",
    region: "MN",
    page: 0,
  },
  data: {
    list: [],
    total: 0,
    classicUser: [],
    totalPage: 0,
  },
  filterPressCount: 0,
  exportPressCount: 0,
  isLoaded: false,
  isLoading: false,
};

const DATE_FORMAT = "YYYY-MM-DD";

const buildDefaultRange = () => {
  const from = moment().startOf("day").toDate();
  const to = moment().add(7, "days").startOf("day").toDate();

  return { from, to };
};

const parseDateFromQuery = (value, fallback) => {
  if (!value) return fallback;

  const parsed = moment(value, [DATE_FORMAT, "DD/MM/YYYY"], true);

  // startOf('day') prevents URL↔state sync loop from time component mismatch
  return parsed.isValid() ? parsed.startOf("day").toDate() : fallback;
};

const parsePageFromQuery = (value) => {
  const parsed = parseInt(value, 10);

  if (Number.isNaN(parsed) || parsed < 0) {
    return 0;
  }

  return parsed;
};

const buildFilterFromSearch = (searchParams) => {
  const { from: defaultFrom, to: defaultTo } = buildDefaultRange();

  return {
    from: parseDateFromQuery(searchParams.get("from"), defaultFrom),
    to: parseDateFromQuery(searchParams.get("to"), defaultTo),
    channelId: searchParams.get("channelId") || "",
    status: searchParams.get("status") || "",
    region: searchParams.get("region") || "MN",
    page: parsePageFromQuery(searchParams.get("page")),
  };
};

const formatDateForQuery = (date) => {
  const m = moment(date);
  return m.isValid() ? m.format(DATE_FORMAT) : "";
};

const buildSearchParamsFromFilter = (filter) => {
  const params = {
    from: formatDateForQuery(filter.from),
    to: formatDateForQuery(filter.to),
    page: parsePageFromQuery(filter.page),
    region: filter.region,
  };

  if (filter.channelId) {
    params.channelId = filter.channelId;
  }

  if (
    filter.status !== "" &&
    filter.status !== undefined &&
    filter.status !== null
  ) {
    params.status = filter.status;
  }

  return params;
};

const normalizeDate = (date) => {
  if (!date) return null;
  const m = moment(date);
  if (!m.isValid()) return null;
  return m.startOf("day").toDate();
};

const reducer = (state, action) => {
  switch (action.type) {
    case "SUBMIT_FILTER": {
      const { from: defaultFrom, to: defaultTo } = buildDefaultRange();
      return {
        ...state,
        filter: {
          ...action.payload,
          from: normalizeDate(action.payload.from) || defaultFrom,
          to: normalizeDate(action.payload.to) || defaultTo,
          page: 0,
        },
        filterPressCount: state.filterPressCount + 1,
      };
    }
    case "REPLACE_FILTER":
      return {
        ...state,
        filter: {
          ...state.filter,
          ...action.payload,
        },
      };
    case "UPDATE_DATA":
      return {
        ...state,
        data: {
          ...action.payload,
        },
        isLoaded: true,
      };
    case "UPDATE_PAGE":
      return {
        ...state,
        filter: {
          ...state.filter,
          page: action.payload,
        },
      };
    case "SET_LOADING":
      return {
        ...state,
        isLoading: Boolean(action.payload),
      };
    default:
      return {
        ...state,
      };
  }
};

const Period = () => {
  const location = useLocation();
  const history = useHistory();
  const lastSyncedSearch = useRef(location.search.replace(/^\?/, ""));

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const initialFilter = useMemo(
    () => buildFilterFromSearch(searchParams),
    [searchParams],
  );

  const [state, dispatch] = useReducer(reducer, {
    ...initialData,
    filter: {
      ...initialData.filter,
      ...initialFilter,
    },
  });

  const [cookies] = useCookies(["lottmekong"]);

  const getAllFunc = useCallback(async () => {
    dispatch({ type: "SET_LOADING", payload: true });
    let response = null;
    try {
      response = await getList(
        cookies.lottmekong,
        {
          from: state.filter.from,
          to: state.filter.to,
          channelId: state.filter.channelId,
          status: state.filter.status,
          region: state.filter.region,
        },
        state.filter.page,
      );
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }

    if (response) {
      dispatch({
        type: "UPDATE_DATA",
        payload: {
          list: response.data,
          total: response.total,
          classicUser: response.classicUser,
          totalPage: calculatePage(response.total),
        },
      });
    }
  }, [
    cookies.lottmekong,
    dispatch,
    state.filter.from,
    state.filter.to,
    state.filter.channelId,
    state.filter.status,
    state.filter.page,
    state.filter.region,
  ]);

  useEffect(() => {
    getAllFunc();
  }, [
    getAllFunc,
    state.filterPressCount,
    state.filter.page,
    state.filter.from,
    state.filter.to,
    state.filter.channelId,
    state.filter.status,
    state.filter.region,
  ]);

  // Sync state -> URL (one-way only)
  useEffect(() => {
    const params = buildSearchParamsFromFilter(state.filter);
    const newSearchParams = new URLSearchParams();
    Object.keys(params).forEach((key) => {
      if (
        params[key] !== undefined &&
        params[key] !== null &&
        params[key] !== ""
      ) {
        newSearchParams.set(key, String(params[key]));
      }
    });
    const newSearch = newSearchParams.toString();

    if (lastSyncedSearch.current !== newSearch) {
      lastSyncedSearch.current = newSearch;
      history.replace({ search: newSearch });
    }
  }, [state.filter, history]);

  useEffect(() => {
    const regionName =
      state.filter.region === "MN"
        ? "Miền Nam"
        : state.filter.region === "MB"
          ? "Miền Bắc"
          : "Miền Trung";
    document.title = `Kỳ quay ${regionName}`;
  }, [state.filter.region]);

  return (
    <Context.Provider
      value={{
        state,
        dispatch,
        updateSearchParams: history,
      }}
    >
      <div className="py-4">
        <FilterForm />

        <div className="mt-4 position-relative">
          {!state.isLoaded ? (
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <>
              <Table region={state.filter.region} />
              {state.isLoading && (
                <div
                  className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
                  style={{
                    background: "rgba(255, 255, 255, 0.6)",
                    backdropFilter: "blur(1px)",
                  }}
                >
                  <Spinner animation="border" variant="primary" />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Context.Provider>
  );
};

export default Period;
